/** API client: always same-origin `/api` in dev (proxied) or via VITE_API_BASE_URL. */
const BASE = ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_API_BASE_URL as string | undefined) ?? "";

/** Base for full-page API redirects (OAuth sign-in, bot invite). Same-origin in dev, absolute in prod. */
export const API_ORIGIN = BASE.replace(/\/$/, "");
/** URL that starts Discord OAuth sign-in for the dashboard. */
export const SIGN_IN_URL = `${API_ORIGIN}/api/auth/discord`;
/** URL that starts the actual bot-add flow (bot + applications.commands). */
export const INVITE_URL = `${API_ORIGIN}/api/invite`;

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (res.status === 401) throw new ApiError(401, "unauthorized");
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, (body as { error?: string }).error ?? "request_failed");
  }
  return (await res.json()) as T;
}

export interface Me { id: string; username: string; avatar: string | null; exp: number }
export interface GuildSummary { id: string; access: string }

export const api = {
  me: () => request<Me>("/api/auth/me"),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  guilds: () => request<{ guilds: GuildSummary[] }>("/api/guilds"),
  guildPermissions: (guildId: string) => request<{ guildId: string; level: string }>(`/api/guilds/${guildId}/permissions`),
  overview: (guildId: string) => request<Overview>(`/api/guilds/${guildId}/overview`),
  verification: (guildId: string) => request<{ config: { verification: Record<string, unknown>; logChannelId: string | null; logChannelEnabled: boolean } }>(`/api/guilds/${guildId}/verification`),
  updateVerification: (guildId: string, body: Record<string, unknown>) => request<{ ok: boolean }>(`/api/guilds/${guildId}/verification`, { method: "PUT", body: JSON.stringify(body) }),
  verificationHistory: (guildId: string, page: number) => request<{ events: VerificationEvent[]; identities: VerifiedIdentity[]; page: number }>(`/api/guilds/${guildId}/verification-history?page=${page}`),
  security: (guildId: string) => request<{ security: Record<string, unknown> | null }>(`/api/guilds/${guildId}/security`),
  updateSecurity: (guildId: string, body: unknown) => request<{ ok: boolean }>(`/api/guilds/${guildId}/security`, { method: "PUT", body: JSON.stringify(body) }),
  whitelist: (guildId: string, page: number, search: string) => request<{ entries: WhitelistEntry[]; page: number }>(`/api/guilds/${guildId}/whitelist?page=${page}&search=${encodeURIComponent(search)}`),
  setWhitelist: (guildId: string, userId: string, status: "approved" | "removed", reason?: string) => request<{ ok: boolean }>(`/api/guilds/${guildId}/whitelist/${userId}`, { method: "POST", body: JSON.stringify({ status, reason }) }),
  incidents: (guildId: string) => request<{ incidents: Incident[] }>(`/api/guilds/${guildId}/incidents`),
  audit: (guildId: string, action?: string) => request<{ events: AuditEvent[] }>(`/api/guilds/${guildId}/audit${action ? `?action=${encodeURIComponent(action)}` : ""}`),
};

export interface Overview {
  identityCounts: Record<string, number>;
  incidents: Incident[];
  cases: Case[];
}
export interface Incident { id: string; type: string; severity: string; status: string; summary: string; action_taken: string | null; created_at: string }
export interface Case { id: string; case_number: number; type: string; target_user_id: string; moderator_id: string; reason: string | null; created_at: string }
export interface WhitelistEntry { id: string; discord_user_id: string; provider: string; provider_user_id: string | null; status: string; created_at: string }
export interface AuditEvent { id: string; action: string; actor_id: string | null; target_id: string | null; severity: string; result: string; created_at: string; metadata: Record<string, unknown> }
export interface VerificationEvent { id: string; discord_user_id: string; ip_hash: string; vpn: boolean; alt_score: number; banned: boolean; reasons: string[]; outcome: string; created_at: string }
export interface VerifiedIdentity { discord_user_id: string; provider: string; provider_username: string | null; state: string; verified_at: string }
